import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import GlobalCallListener from "@/components/GlobalCallListener";
import { PractitionerBottomNav } from "@/components/PractitionerDashboardView";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard, Users, MessageCircle, BarChart3, Settings, LogOut, User, Wallet,
} from "lucide-react";

interface DietitianLayoutProps { children: ReactNode; }

const navItems = [
  { path: "/dietitian", icon: LayoutDashboard, labelEn: "Dashboard", labelBn: "ড্যাশবোর্ড", end: true },
  { path: "/dietitian/patients", icon: Users, labelEn: "Patients", labelBn: "রোগীরা", end: false },
  { path: "/dietitian/inbox", icon: MessageCircle, labelEn: "Inbox", labelBn: "ইনবক্স", end: false },
  { path: "/dietitian/wallet", icon: Wallet, labelEn: "Wallet", labelBn: "ওয়ালেট", end: false },
  { path: "/dietitian/analytics", icon: BarChart3, labelEn: "Analytics", labelBn: "বিশ্লেষণ", end: false },
  { path: "/dietitian/settings", icon: Settings, labelEn: "Settings", labelBn: "সেটিংস", end: false },
];

const DietitianLayout = ({ children }: DietitianLayoutProps) => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const bn = language === "bn";
  const location = useLocation();
  const [profile, setProfile] = useState<{ full_name: string; profile_photo: string }>({ full_name: "", profile_photo: "" });

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("full_name, profile_photo").eq("user_id", user.id).single()
      .then(({ data }) => { if (data) setProfile(data as any); });
  }, [user]);

  const handleLogout = async () => { await logout(); window.location.href = "/login"; };

  const ProfileAvatar = () => (
    profile.profile_photo ? (
      <img src={profile.profile_photo} alt="" className="h-9 w-9 rounded-xl object-cover" />
    ) : (
      <div className="h-9 w-9 rounded-xl gradient-primary flex items-center justify-center"><User className="h-4 w-4 text-primary-foreground" /></div>
    )
  );

  return (
    <div className="gradient-bg flex overflow-x-hidden" style={{ minHeight: '100dvh' }}>
      <div className="flex-1 flex flex-col overflow-x-hidden" style={{ height: '100dvh' }}>
        {!location.pathname.includes("/chat/") && (
          <>
            <header className="sticky top-0 z-30 flex items-center justify-center border-b border-white/70 bg-white/70 px-4 py-3 backdrop-blur-xl md:hidden">
              <h1 className="font-extrabold text-sm">{navItems.find((i) => i.end ? location.pathname === i.path : location.pathname.startsWith(i.path) && i.path !== "/dietitian")?.labelEn || navItems[0].labelEn}</h1>
              <div className="absolute right-4"><LanguageToggle /></div>
            </header>
            <header className="sticky top-0 z-30 hidden items-center justify-between border-b border-white/70 bg-white/70 px-6 py-4 backdrop-blur-xl md:flex">
              <h1 className="font-serif text-lg font-extrabold">{bn ? navItems.find((i) => i.end ? location.pathname === i.path : location.pathname.startsWith(i.path) && i.path !== "/dietitian")?.labelBn || navItems[0].labelBn : navItems.find((i) => i.end ? location.pathname === i.path : location.pathname.startsWith(i.path) && i.path !== "/dietitian")?.labelEn || navItems[0].labelEn}</h1>
              <LanguageToggle />
            </header>
          </>
        )}
        <main className={location.pathname.includes("/chat/") ? "flex-1 overflow-hidden" : "flex-1 overflow-y-auto p-4 pb-24 md:p-6 md:pb-6"}>{children}</main>
      </div>
      <PractitionerBottomNav practitionerType="dietitian" bn={bn} />
      <GlobalCallListener practitionerType="dietitian" />
    </div>
  );
};

export default DietitianLayout;
